<template>
    <v-row>
        <v-col cols="12" sm="12" md="6">
            <label>{{ fromLabel }}</label>
            <Datepicker
                v-model="dateRange.from"
                :enable-time-picker="false"
                :format="datepickerPreviewFormat"
                :class="{ 'validation-error-border': validation.dateFromError }"
            ></Datepicker>
            <div v-if="validation.dateFromError" class="validation-error">
                {{ validation.dateFromError }}
            </div>
        </v-col>
        <v-col cols="12" sm="12" md="6">
            <label>{{ toLabel }}</label>
            <Datepicker
                v-model="dateRange.to"
                :enable-time-picker="false"
                :format="datepickerPreviewFormat"
                :class="{ 'validation-error-border': validation.dateToError }"
            ></Datepicker>
            <div v-if="validation.dateToError" class="validation-error">
                {{ validation.dateToError }}
            </div>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, computed } from 'vue';
import dayjs from 'dayjs';
import { Formatter } from '../utils/Formatter';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

interface DateRange {
    from: Date | null;
    to: Date | null;
}

interface ValidationState {
    dateFromError: string;
    dateToError: string;
    hasErrors: boolean;
}

export default defineComponent({
    name: 'DateRangeSelect',

    components: {
        Datepicker
    },

    props: {
        modelValue: {
            type: Object as () => DateRange,
            required: true
        },
        fromLabel: {
            type: String,
            required: true
        },
        toLabel: {
            type: String,
            required: true
        },
        enableValidation: {
            type: Boolean,
            default: true
        }
    },

    emits: ['update:modelValue', 'validation-change'],

    setup(props, { emit }) {
        const validation = reactive<ValidationState>({
            dateFromError: '',
            dateToError: '',
            hasErrors: false
        });

        const dateRange = computed({
            get: () => props.modelValue,
            set: (value: DateRange) => {
                emit('update:modelValue', value);
            }
        });

        const validateDateRange = () => {
            if (!props.enableValidation) {
                validation.dateFromError = '';
                validation.dateToError = '';
                validation.hasErrors = false;
                emit('validation-change', { hasErrors: false });
                return;
            }

            validation.dateFromError = '';
            validation.dateToError = '';
            validation.hasErrors = false;

            const dateFrom = dateRange.value.from;
            const dateTo = dateRange.value.to;

            if (dateFrom && !dayjs(dateFrom).isValid()) {
                validation.dateFromError = 'Invalid date format';
                validation.hasErrors = true;
            }

            if (dateTo && !dayjs(dateTo).isValid()) {
                validation.dateToError = 'Invalid date format';
                validation.hasErrors = true;
            }

            if (dateFrom && dateTo && dayjs(dateTo).isBefore(dayjs(dateFrom))) {
                validation.dateFromError = 'From date must be earlier than or equal to To date';
                validation.hasErrors = true;
            }

            emit('validation-change', { hasErrors: validation.hasErrors });
        };

        watch(
            () => [dateRange.value.from, dateRange.value.to],
            () => {
                validateDateRange();
            }
        );

        return {
            dateRange,
            validation,
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            validateDateRange
        };
    }
});
</script>

<style scoped>
.validation-error {
    color: red;
    font-size: 12px;
    margin-top: 4px;
}

.validation-error-border {
    border: 1px solid red !important;
}
</style>
